/**
 * Refund Receipt — printable A5/A4 receipt for a single refund entry on an invoice.
 *
 * Route: /print/refund-receipt/:invoiceId/:refundNo
 * Auto-prints. Append ?autoprint=0 to preview.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BRAND } from "@/lib/api";
import PrintHeader from "@/components/PrintHeader";
import LetterheadToggle, { useLetterheadMode } from "@/components/LetterheadToggle";
import { useUserNames } from "@/lib/useUserNames";

// Words conversion for INR (paise-precision rounded to nearest rupee)
const oneToNineteen = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
function lessThanHundred(n) {
  if (n < 20) return oneToNineteen[n];
  return `${tens[Math.floor(n / 10)]}${n % 10 ? " " + oneToNineteen[n % 10] : ""}`;
}
function numToWords(num) {
  num = Math.round(num);
  if (num === 0) return "Zero";
  const parts = [];
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  const hundred = Math.floor(num / 100); num %= 100;
  if (crore) parts.push(`${lessThanHundred(crore)} Crore`);
  if (lakh) parts.push(`${lessThanHundred(lakh)} Lakh`);
  if (thousand) parts.push(`${lessThanHundred(thousand)} Thousand`);
  if (hundred) parts.push(`${oneToNineteen[hundred]} Hundred`);
  if (num) parts.push(lessThanHundred(num));
  return parts.join(" ");
}

export default function PrintRefundReceipt() {
  const { invoiceId, refundNo } = useParams();
  const [inv, setInv] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);
  const letterhead = useLetterheadMode();
  const nameOf = useUserNames();

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setHospital(me.data.hospital);
        setBranch(me.data.branch);
        const { data } = await api.get(`/billing/invoices/${invoiceId}`);
        setInv(data);
        const auto = new URLSearchParams(window.location.search).get("autoprint");
        if (auto !== "0") setTimeout(() => window.print(), 700);
      } catch (e) { console.error("Refund receipt load failed", e); }
    })();
  }, [invoiceId]);

  if (!inv) return <div className="p-10 text-sm text-ink-muted">Loading refund receipt…</div>;

  // Resolve refund entry. Prefer match-by-refund_no (stable); fall back to numeric index.
  const refunds = Array.isArray(inv.refunds) ? inv.refunds : [];
  let refund = refunds.find((r) => r.refund_no === refundNo);
  if (!refund && /^\d+$/.test(refundNo || "")) refund = refunds[Number(refundNo)];
  if (!refund) refund = refunds[refunds.length - 1];

  if (!refund) {
    return <div className="p-10 text-sm text-rose-700">No refund entries on this invoice.</div>;
  }

  const amt = Number(refund.amount || 0);
  const dt = (refund.refunded_at || "").slice(0, 16).replace("T", " ");

  return (
    <>
      <style>{`
        @page { size: A5; margin: 8mm; }
        body { background: white; }
        @media print {
          .no-print { display: none !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
      <div className="mx-auto bg-white text-[12px] leading-snug text-ink" style={{ width: "138mm", padding: "0" }} data-testid="print-refund-receipt">
        <LetterheadToggle />
        {!letterhead && (
          <PrintHeader
            compact
            hospital={hospital}
            branch={branch}
            subtitle="Refund Receipt"
            right={
              <>
                <div>Refund No.: <b className="font-mono">{refund.refund_no || "—"}</b></div>
                <div>Date: <b>{dt || "—"}</b></div>
              </>
            }
          />
        )}

        {/* DUPLICATE / ORIGINAL strip — rose accent */}
        <div className="mt-3 rounded-md px-3 py-2 text-center" style={{ background: "#FEE2E2", border: "1px solid #FECACA", color: "#9F1239" }}>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold">Refund Receipt · Reverse / Adjustment</div>
        </div>

        {/* Patient + invoice */}
        <div className="mt-3 grid grid-cols-2 gap-3 text-[11px]">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-ink-muted">Patient</div>
            <div className="font-semibold">{inv.patient_name}</div>
            <div className="font-mono text-[10px] text-ink-muted">UHID: {inv.patient_uhid}</div>
          </div>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-wider text-ink-muted">Against invoice</div>
            <div className="font-mono">{inv.invoice_no}</div>
            <div className="text-[10px] text-ink-muted">Invoiced ₹ {Number(inv.total || 0).toFixed(2)} · Paid ₹ {Number(inv.amount_paid || 0).toFixed(2)}</div>
          </div>
        </div>

        {/* Refund details */}
        <table className="mt-4 w-full border-collapse text-[12px]">
          <tbody>
            <tr style={{ background: "#FDF2F8" }}>
              <td className="px-3 py-2 font-semibold w-1/3">Amount refunded</td>
              <td className="px-3 py-2 text-right font-heading text-lg" style={{ color: "#BE123C" }}>₹ {amt.toFixed(2)}</td>
            </tr>
            <tr className="border-b border-border">
              <td className="px-3 py-2 font-semibold">In words</td>
              <td className="px-3 py-2">Rupees {numToWords(amt)} only</td>
            </tr>
            <tr className="border-b border-border">
              <td className="px-3 py-2 font-semibold">Mode</td>
              <td className="px-3 py-2 capitalize">{refund.mode || "cash"}</td>
            </tr>
            {refund.reference && (
              <tr className="border-b border-border">
                <td className="px-3 py-2 font-semibold">Reference / UTR</td>
                <td className="px-3 py-2 font-mono text-[11px]">{refund.reference}</td>
              </tr>
            )}
            <tr className="border-b border-border">
              <td className="px-3 py-2 font-semibold">Reason</td>
              <td className="px-3 py-2">{refund.reason || "—"}</td>
            </tr>
            <tr>
              <td className="px-3 py-2 font-semibold">Processed by</td>
              <td className="px-3 py-2">{refund.refunded_by_name || nameOf(refund.refunded_by, "—")}</td>
            </tr>
          </tbody>
        </table>

        {/* Running totals for audit */}
        <div className="mt-3 rounded-md border border-border bg-secondary/30 p-2 text-[10.5px]">
          <div className="flex justify-between"><span className="text-ink-muted">Total refunded on this invoice till date</span><span className="font-semibold">₹ {Number(inv.refund_amount || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-ink-muted">Net retained by hospital</span><span className="font-semibold">₹ {(Number(inv.amount_paid || 0) - Number(inv.refund_amount || 0)).toFixed(2)}</span></div>
        </div>

        {/* Signatures */}
        <div className="mt-10 grid grid-cols-2 gap-6 text-[10.5px]">
          <div>
            <div className="border-t border-ink pt-1">Patient / Recipient signature</div>
          </div>
          <div className="text-right">
            <div className="border-t border-ink pt-1">Cashier / Authorised signatory</div>
          </div>
        </div>

        <div className="mt-6 border-t border-border pt-2 text-[9px] text-ink-muted flex justify-between">
          <div>{hospital?.name || BRAND.name} · {branch?.name || ""} · Refund Receipt</div>
          <div>Printed: {new Date().toISOString().slice(0, 16).replace("T", " ")}</div>
        </div>
        <div className="mt-1 text-center text-[8.5px] text-ink-muted">{BRAND.footer}</div>

        <div className="no-print fixed bottom-6 right-6">
          <button
            onClick={() => window.print()}
            className="rounded-md bg-rose-600 px-4 py-2 text-white shadow-lg hover:bg-rose-700"
            data-testid="refund-receipt-print-btn"
          >
            Print refund receipt
          </button>
        </div>
      </div>
    </>
  );
}
